import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, FileText, Package, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getNews, createNews, updateNews, deleteNews, getNewsCategories, uploadFile } from "@/lib/api";
import { formatDate as formatDateUtil } from "@/lib/dateUtils";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExtension from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";

// TipTap Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL invoeren:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-100 rounded-t-lg">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 px-2 ${editor.isActive("bold") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 px-2 ${editor.isActive("italic") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`h-8 px-2 ${editor.isActive("underline") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Underline className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`h-8 px-2 font-bold ${editor.isActive("heading", { level: 2 }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        H2
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`h-8 px-2 font-bold ${editor.isActive("heading", { level: 3 }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        H3
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 px-2 ${editor.isActive("bulletList") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 px-2 ${editor.isActive("orderedList") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "left" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "center" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "right" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addLink}
        className={`h-8 px-2 ${editor.isActive("link") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

const initialFormData = {
  title: "",
  summary: "",
  content: "",
  image_url: "",
  category: "general",
  is_featured: false,
  published_at: new Date().toISOString().slice(0, 16),
};

export default function AdminNews() {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[200px] p-4 focus:outline-none",
      },
    },
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Alleen afbeeldingen zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFile(file);
      setFormData({...formData, image_url: res.data.url});
      toast.success("Afbeelding geüpload");
    } catch (error) {
      toast.error("Kon afbeelding niet uploaden");
    } finally {
      setUploading(false);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await getNews();
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getNewsCategories();
        setCategories(res.data.filter(c => c.is_active));
      } catch (e) {
        console.error("Error fetching news categories:", e);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchNews();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, is_featured: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const submitData = {
      ...formData,
      content: editor?.getHTML() || "",
    };

    try {
      if (editingId) {
        await updateNews(editingId, submitData);
        toast.success("Article updated successfully");
      } else {
        await createNews(submitData);
        toast.success("Article created successfully");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      editor?.commands.setContent("");
      setEditingId(null);
      fetchNews();
    } catch (error) {
      toast.error("Failed to save article");
      console.error("Error saving article:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (article) => {
    setFormData({
      title: article.title,
      summary: article.summary,
      content: article.content,
      image_url: article.image_url || "",
      category: article.category,
      is_featured: article.is_featured,
      published_at: article.published_at ? new Date(article.published_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
    });
    editor?.commands.setContent(article.content || "");
    setEditingId(article.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;

    try {
      await deleteNews(id);
      toast.success("Article deleted successfully");
      fetchNews();
    } catch (error) {
      toast.error("Failed to delete article");
      console.error("Error deleting article:", error);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    editor?.commands.setContent("");
    setEditingId(null);
    setDialogOpen(true);
  };

  const formatDate = (dateStr) => formatDateUtil(dateStr);

  return (
    <div data-testid="admin-news-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900 font-['Chivo'] tracking-tight">Nieuws</h1>
          <p className="text-slate-500 font-['Manrope'] mt-1">Beheer nieuwsartikelen</p>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 shadow-sm font-['Manrope']" data-testid="add-news-btn">
          <Plus className="h-4 w-4 mr-2" />
          Artikel Toevoegen
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Titel</TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Categorie</TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Uitgelicht</TableHead>
                  <TableHead className="text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Gepubliceerd</TableHead>
                  <TableHead className="text-right text-slate-500 font-semibold font-['Manrope'] uppercase text-xs tracking-wider">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : news.length > 0 ? (
                  news.map((article) => (
                    <TableRow key={article.id} data-testid={`news-row-${article.id}`}>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-medium truncate">{article.title}</p>
                          <p className="text-sm text-slate-500 truncate">{article.summary}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{article.category}</Badge>
                      </TableCell>
                      <TableCell>
                        {article.is_featured && (
                          <Badge className="bg-yellow-500">Featured</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(article.published_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(article)}
                            data-testid={`edit-news-${article.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(article.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-news-${article.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Geen artikelen gevonden. Voeg je eerste artikel toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Artikel Bewerken" : "Artikel Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-700">Titel</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                data-testid="news-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary" className="text-slate-700">Samenvatting</Label>
              <Textarea
                id="summary"
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                required
                rows={2}
                data-testid="news-summary"
                className="border-slate-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content" className="text-slate-700">Inhoud</Label>
              <div className="border border-slate-300 rounded-lg bg-white overflow-hidden">
                <EditorToolbar editor={editor} />
                <div className="min-h-[200px]">
                  <EditorContent editor={editor} data-testid="news-content" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url" className="text-slate-700">Afbeelding</Label>
              <div className="flex gap-2">
                <Input
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="URL of upload een afbeelding"
                  className="flex-1 border-slate-300"
                  data-testid="news-image"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-slate-300"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? "..." : "Upload"}
                </Button>
              </div>
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg mt-2" />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="published_at" className="text-slate-700">Publicatiedatum</Label>
              <Input
                id="published_at"
                name="published_at"
                type="datetime-local"
                value={formData.published_at}
                onChange={handleChange}
                className="border-slate-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Categorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => handleSelectChange("category", v)}
                >
                  <SelectTrigger data-testid="news-category" className="border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.code}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Uitgelicht</Label>
                <div className="flex items-center h-10">
                  <Switch 
                    checked={formData.is_featured} 
                    onCheckedChange={handleSwitchChange}
                    data-testid="news-featured"
                  />
                  <span className="ml-2 text-sm text-slate-500">
                    {formData.is_featured ? "Ja" : "Nee"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">
                Annuleren
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="news-save-btn">
                {saving ? "Opslaan..." : "Opslaan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
